import { ComponentChild } from 'preact';
import clsx from 'clsx';
import { CSSProperties, PropsWithChildren } from 'preact/compat';
import { variationName } from 'utilities/css';

import styles from './Text.module.scss';

type HtmlElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';

type Size = 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge';

type Variation = 'subdued' | 'strong' | 'semiStrong';

type Color = 'inherit' | 'critical' | 'success' | 'interactive';

interface TextProps {
  variation?: Variation;
  /** Text size
   *
   * extraSmall - 12px,
   * small - 14px,
   * medium - 16px,
   * large - 18px,
   * extraLarge - 24px
   */
  size?: Size;
  /** HTML element
   * @default 'p'
   */
  as?: HtmlElement;
  color?: Color;
  children: ComponentChild;
  truncate?: boolean;
  lineClamp?: number;
  underline?: boolean;
  uppercase?: boolean;
  classNames?: string[];
}

export function Text({
  variation,
  size = 'medium',
  as = 'p',
  color,
  children,
  truncate,
  lineClamp,
  underline,
  uppercase,
  classNames = [],
}: PropsWithChildren<TextProps>) {
  const Component = as;
  const className = clsx(
    styles.Text,
    color && styles[variationName('color', color)],
    truncate && styles.block && styles.truncate,
    variation && styles[variationName('variation', variation)],
    size && styles[variationName('size', size)],
    lineClamp && styles.lineClamp,
    underline && styles.underline,
    uppercase && styles.uppercase,
    ...classNames,
  );

  const style: CSSProperties = {};

  if (lineClamp) {
    style.webkitLineClamp = lineClamp;
  }

  return (
    <Component style={style} className={className}>
      {children}
    </Component>
  );
}
